import React, { useCallback, useEffect, useRef, useState } from "react";
import { BarChart3, FileSignature, GitBranch, Loader2, Play, Plus, Save, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { whatsappMerkez } from "@/lib/api";
import { Button } from "@/components/ui/button";

const NODE_TYPES = [
    { v: "intent", l: "Niyet eşleşmesi" },
    { v: "menu", l: "Menü seçenekleri" },
    { v: "template", l: "Şablon yanıt" },
    { v: "action", l: "Aksiyon (vekalet/OTP/CRM)" },
    { v: "condition", l: "Koşul dallanma" },
];

const ACTION_OPTIONS = [
    { v: "", l: "— Yok —" },
    { v: "vekalet_gonder", l: "Vekalet PDF gönder" },
    { v: "otp_baslat", l: "OTP / TC başlat" },
    { v: "sessiz_mod", l: "Sessiz mod (operatör)" },
    { v: "menu_ac", l: "Menü aç" },
    { v: "sablon:eksik_evrak", l: "Şablon: eksik evrak" },
    { v: "sablon:vekalet_hatirlatma", l: "Şablon: vekalet hatırlatma" },
    { v: "crm_etiket:vekalet", l: "CRM etiket: vekalet" },
];

const BOT_MODLARI = [
    { v: "bot_oncelik", l: "Bot öncelikli" },
    { v: "hibrit", l: "Hibrit (Bot + Akıl)" },
    { v: "akil_oncelik", l: "Akıl öncelikli" },
];

export default function WhatsAppInboxBotFlowEditor() {
    const [flows, setFlows] = useState([]);
    const [secili, setSecili] = useState(null);
    const [busy, setBusy] = useState(false);
    const [testMesaj, setTestMesaj] = useState("merhaba");
    const [testSonuc, setTestSonuc] = useState(null);
    const [analitik, setAnalitik] = useState(null);
    const [vekaletInfo, setVekaletInfo] = useState(null);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        const data = await whatsappMerkez.inboxBotAkislari();
        const rows = data.akislari || [];
        setFlows(rows);
        if (!secili && rows[0]) setSecili(rows[0]);
    }, [secili]);

    useEffect(() => {
        load().catch(() => toast.error("Bot akışları yüklenemedi."));
        whatsappMerkez.inboxVekaletOrnek().then(setVekaletInfo).catch(() => {});
        whatsappMerkez.inboxBotAnalitik(7).then(setAnalitik).catch(() => {});
    }, [load]);

    async function testAkis() {
        if (!secili) return;
        try {
            const res = await whatsappMerkez.inboxBotTest({
                mesaj: testMesaj,
                flow_id: secili.id,
                akis: secili.akis,
            });
            setTestSonuc(res);
        } catch {
            toast.error("Test başarısız.");
        }
    }

    async function vekaletYukle(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        try {
            const res = await whatsappMerkez.inboxVekaletOrnekYukle(f);
            setVekaletInfo(res);
            toast.success("Vekalet şablonu yüklendi.");
        } catch {
            toast.error("Yükleme hatası.");
        }
    }

    async function kaydet() {
        if (!secili) return;
        setBusy(true);
        try {
            const res = await whatsappMerkez.inboxBotAkisKaydet(secili);
            if (res?.ok !== false) {
                toast.success("Bot akışı kaydedildi.");
                await load();
            } else toast.error(res?.detail || "Kaydedilemedi.");
        } catch {
            toast.error("Kayıt hatası.");
        } finally {
            setBusy(false);
        }
    }

    function nodeGuncelle(idx, patch) {
        if (!secili?.akis?.nodes) return;
        const nodes = [...secili.akis.nodes];
        nodes[idx] = { ...nodes[idx], ...patch };
        setSecili({ ...secili, akis: { ...secili.akis, nodes } });
    }

    function nodeEkle() {
        const id = `node_${Date.now()}`;
        const nodes = [...(secili?.akis?.nodes || []), {
            id,
            type: "intent",
            label: "Yeni adım",
            intents: [],
            reply: "",
        }];
        setSecili({
            ...secili,
            akis: { ...(secili?.akis || {}), nodes, start_node_id: secili?.akis?.start_node_id || id },
        });
    }

    function nodeSil(idx) {
        const nodes = (secili?.akis?.nodes || []).filter((_, i) => i !== idx);
        setSecili({ ...secili, akis: { ...secili.akis, nodes } });
    }

    function menuOptionGuncelle(nodeIdx, optIdx, patch) {
        if (!secili?.akis?.nodes) return;
        const nodes = [...secili.akis.nodes];
        const options = [...(nodes[nodeIdx].options || [])];
        options[optIdx] = { ...options[optIdx], ...patch };
        nodes[nodeIdx] = { ...nodes[nodeIdx], options };
        setSecili({ ...secili, akis: { ...secili.akis, nodes } });
    }

    function menuOptionEkle(nodeIdx) {
        if (!secili?.akis?.nodes) return;
        const nodes = [...secili.akis.nodes];
        const options = [...(nodes[nodeIdx].options || []), { match: [], reply: "", next: "" }];
        nodes[nodeIdx] = { ...nodes[nodeIdx], options };
        setSecili({ ...secili, akis: { ...secili.akis, nodes } });
    }

    function menuOptionSil(nodeIdx, optIdx) {
        if (!secili?.akis?.nodes) return;
        const nodes = [...secili.akis.nodes];
        const options = (nodes[nodeIdx].options || []).filter((_, i) => i !== optIdx);
        nodes[nodeIdx] = { ...nodes[nodeIdx], options };
        setSecili({ ...secili, akis: { ...secili.akis, nodes } });
    }

    const nodeIds = (secili?.akis?.nodes || []).map((n) => n.id);

    if (!secili && flows.length === 0) {
        return (
            <div className="wa-bot-editor wa-bot-editor--empty">
                <Loader2 className="h-6 w-6 animate-spin" />
            </div>
        );
    }

    return (
        <div className="wa-bot-editor">
            <div className="wa-bot-editor__sidebar">
                <div className="wa-bot-editor__sidebar-head">
                    <GitBranch className="h-4 w-4" />
                    Bot akışları
                </div>
                {flows.map((f) => (
                    <button
                        key={f.id}
                        type="button"
                        className={`wa-bot-editor__flow-item ${secili?.id === f.id ? "is-active" : ""}`}
                        onClick={() => setSecili(f)}
                    >
                        <span>{f.ad}</span>
                        <span className="wa-bot-editor__flow-meta">{f.aktif ? "Aktif" : "Kapalı"}</span>
                    </button>
                ))}
            </div>

            {secili ? (
                <div className="wa-bot-editor__main">
                    <div className="wa-bot-editor__toolbar">
                        <input
                            value={secili.ad || ""}
                            onChange={(e) => setSecili({ ...secili, ad: e.target.value })}
                            className="wa-bot-editor__title-input"
                        />
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={!!secili.aktif}
                                onChange={(e) => setSecili({ ...secili, aktif: e.target.checked })}
                            />
                            Aktif
                        </label>
                        <Button size="sm" onClick={kaydet} disabled={busy}>
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            Kaydet
                        </Button>
                        <Button size="sm" variant="outline" onClick={nodeEkle}>
                            <Plus className="h-4 w-4" /> Adım
                        </Button>
                    </div>

                    <div className="wa-bot-editor__start">
                        <label>Bot modu</label>
                        <select
                            value={secili.akis?.bot_modu || "hibrit"}
                            onChange={(e) =>
                                setSecili({
                                    ...secili,
                                    akis: { ...secili.akis, bot_modu: e.target.value },
                                })
                            }
                        >
                            {BOT_MODLARI.map((m) => (
                                <option key={m.v} value={m.v}>
                                    {m.l}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="wa-bot-editor__start">
                        <label>Başlangıç node</label>
                        <select
                            value={secili.akis?.start_node_id || ""}
                            onChange={(e) =>
                                setSecili({
                                    ...secili,
                                    akis: { ...secili.akis, start_node_id: e.target.value },
                                })
                            }
                        >
                            {(secili.akis?.nodes || []).map((n) => (
                                <option key={n.id} value={n.id}>
                                    {n.label || n.id}
                                </option>
                            ))}
                        </select>
                    </div>

                    <textarea
                        className="wa-bot-editor__mesai"
                        rows={2}
                        value={secili.mesai_disi_mesaj || ""}
                        onChange={(e) => setSecili({ ...secili, mesai_disi_mesaj: e.target.value })}
                        placeholder="Mesai dışı otomatik yanıt…"
                    />

                    <div className="wa-bot-editor__nodes">
                        {(secili.akis?.nodes || []).map((node, idx) => (
                            <div key={node.id} className="wa-bot-editor__node">
                                <div className="wa-bot-editor__node-head">
                                    <input
                                        value={node.label || ""}
                                        onChange={(e) => nodeGuncelle(idx, { label: e.target.value })}
                                        placeholder="Adım adı"
                                    />
                                    <select
                                        value={node.type || "intent"}
                                        onChange={(e) => nodeGuncelle(idx, { type: e.target.value })}
                                    >
                                        {NODE_TYPES.map((t) => (
                                            <option key={t.v} value={t.v}>
                                                {t.l}
                                            </option>
                                        ))}
                                    </select>
                                    <button type="button" onClick={() => nodeSil(idx)} title="Sil">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                                <input
                                    value={(node.intents || []).join(", ")}
                                    onChange={(e) =>
                                        nodeGuncelle(idx, {
                                            intents: e.target.value.split(",").map((x) => x.trim()).filter(Boolean),
                                        })
                                    }
                                    placeholder="Niyetler: selamlama, evrak, durum…"
                                />
                                <textarea
                                    rows={3}
                                    value={node.reply || ""}
                                    onChange={(e) => nodeGuncelle(idx, { reply: e.target.value })}
                                    placeholder="Otomatik yanıt metni…"
                                />
                                {(node.type === "action" || node.type === "intent") && (
                                    <select
                                        value={node.action || ""}
                                        onChange={(e) => nodeGuncelle(idx, { action: e.target.value })}
                                        className="mt-2"
                                    >
                                        {ACTION_OPTIONS.map((a) => (
                                            <option key={a.v} value={a.v}>
                                                {a.l}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                {node.type === "condition" && (
                                    <select
                                        value={node.condition || "vekalet_istegi"}
                                        onChange={(e) => nodeGuncelle(idx, { condition: e.target.value })}
                                        className="mt-2"
                                    >
                                        <option value="vekalet_istegi">Vekalet isteği</option>
                                        <option value="mesai_dis">Mesai dışı</option>
                                    </select>
                                )}
                                <input
                                    className="mt-2"
                                    value={node.next || ""}
                                    onChange={(e) => nodeGuncelle(idx, { next: e.target.value })}
                                    placeholder="Sonraki node id"
                                />
                                {node.type === "menu" && (
                                    <div className="wa-bot-editor__menu-options">
                                        <label className="text-xs text-slate-400 block mb-2">
                                            Menü seçenekleri (eşleşme kelimeleri virgülle)
                                        </label>
                                        {(node.options || []).map((opt, oi) => (
                                            <div key={oi} className="wa-bot-editor__menu-option">
                                                <input
                                                    value={(opt.match || []).join(", ")}
                                                    onChange={(e) =>
                                                        menuOptionGuncelle(idx, oi, {
                                                            match: e.target.value
                                                                .split(",")
                                                                .map((x) => x.trim())
                                                                .filter(Boolean),
                                                        })
                                                    }
                                                    placeholder="1, durum, dosya…"
                                                />
                                                <select
                                                    value={opt.next || ""}
                                                    onChange={(e) =>
                                                        menuOptionGuncelle(idx, oi, { next: e.target.value })
                                                    }
                                                >
                                                    <option value="">— Sonraki adım —</option>
                                                    {nodeIds.map((nid) => (
                                                        <option key={nid} value={nid}>
                                                            {nid}
                                                        </option>
                                                    ))}
                                                </select>
                                                <button type="button" onClick={() => menuOptionSil(idx, oi)} title="Sil">
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                                <textarea
                                                    value={opt.reply || ""}
                                                    onChange={(e) =>
                                                        menuOptionGuncelle(idx, oi, { reply: e.target.value })
                                                    }
                                                    placeholder="Bu seçenek için yanıt…"
                                                />
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className="wa-bot-editor__menu-add"
                                            onClick={() => menuOptionEkle(idx)}
                                        >
                                            + Seçenek ekle
                                        </button>
                                        <textarea
                                            rows={2}
                                            value={node.fallback_reply || ""}
                                            onChange={(e) => nodeGuncelle(idx, { fallback_reply: e.target.value })}
                                            placeholder="Eşleşme yoksa fallback yanıt…"
                                            className="mt-2"
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="wa-bot-editor__panel">
                        <div className="wa-bot-editor__panel-head">
                            <Play className="h-4 w-4" /> Test simülasyonu
                        </div>
                        <div className="wa-bot-editor__test">
                            <input
                                value={testMesaj}
                                onChange={(e) => setTestMesaj(e.target.value)}
                                placeholder="Test mesajı: merhaba, vekalet, 5…"
                            />
                            <Button size="sm" variant="outline" onClick={testAkis}>
                                Test et
                            </Button>
                        </div>
                        {testSonuc && (
                            <pre className="wa-bot-editor__test-out">
                                Niyet: {testSonuc.intent_label}
                                {"\n"}
                                Yanıt: {testSonuc.yanit || "(medya/aksiyon veya sessiz)"}
                            </pre>
                        )}
                    </div>

                    <div className="wa-bot-editor__panel">
                        <div className="wa-bot-editor__panel-head">
                            <FileSignature className="h-4 w-4" /> Vekalet örneği (MBS logo)
                        </div>
                        {vekaletInfo?.pdf_url && (
                            <a href={vekaletInfo.pdf_url} target="_blank" rel="noreferrer" className="wa-bot-editor__link">
                                PDF önizle →
                            </a>
                        )}
                        <input ref={fileRef} type="file" accept=".docx" hidden onChange={vekaletYukle} />
                        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
                            <Upload className="h-4 w-4" /> DOCX yükle
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                                whatsappMerkez.inboxVekaletOrnekUret().then((r) => {
                                    setVekaletInfo(r);
                                    toast.success("Varsayılan şablon üretildi.");
                                })
                            }
                        >
                            Varsayılanı üret
                        </Button>
                    </div>

                    {analitik && (
                        <div className="wa-bot-editor__panel">
                            <div className="wa-bot-editor__panel-head">
                                <BarChart3 className="h-4 w-4" /> Bot analitiği (7 gün)
                            </div>
                            <p className="wa-bot-editor__hint">Toplam olay: {analitik.toplam}</p>
                            <ul className="wa-bot-editor__stats">
                                {(analitik.node_istatistik || []).slice(0, 8).map((s, i) => (
                                    <li key={i}>
                                        {s.node_id || "—"} · {s.aksiyon}: {s.adet}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
}
